import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import ButtonMinusIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import ButtonPlusIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';
// styles
import { DescriptionListStyled } from './styles';

export default () => (
  <SectionContainer
    id='whole-life-insurance-faq'
    backgroundVariant='lightB'
    themeType='lightTheme'
    moduleName='faq-module'
    moduleVariant='A'>
    <Row align='center'>
      <Col
        align='center'
        medium={10}
        large={8}>
        <Typography
          component='h2'
          nextElement='typography'>
          You&#39;ve got whole life insurance questions, <WidowBlock>we&#39;ve got answers.</WidowBlock>
        </Typography>
      </Col>
      <Col
        id='faq-col-control'
        medium={10}
        large={8}>
        <Accordion
          id='faq-whole-life-insurance-control'
          variant='secondary'
          toggleOpenIcon={ButtonPlusIcon}
          toggleCloseIcon={ButtonMinusIcon}
          isFAQ
          items={[
            {
              id: 'whole-life-insurance-faq-what-is-it-cash-value',
              title: (<>What is the cash value of whole <WidowBlock>life insurance&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    As you make payments, part of your premium is set aside. Over time, this pool of money (called cash value) typically grows tax deferred. You have the option to withdraw or borrow against it for whatever you need throughout your life. You can use it for things like unexpected expenses, helping to put your kids through college, expanding your business, upgrading your home, or as additional income in retirement. See if whole life is right for you.{' '}
                    <Link
                      id='whole-life-insurance-faq-whats-cash-value-fafa-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Talk to one of our Northwestern Mutual Financial Advisors'>
                      Talk to one of our advisors
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-how-does-it-work-control',
              title: (<>How does whole life <WidowBlock>insurance work&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    We promise to pay who you name (beneficiaries) a set amount of money when you&#39;re no longer here. You, in return, promise to make regular payments (premiums). But whole life insurance from Northwestern Mutual is much more than that. As you make payments, you&#39;ll accumulate cash value (usually tax deferred) that you can use for anything you wish.<sup>2</sup> You can also earn dividends (while not guaranteed, we&#39;ve paid them every year since 1872) that can be taken as cash, used to pay premiums, or buy more coverage.{' '}
                    <Link
                      id='whole-life-insurance-faq-how-does-it-work-link'
                      linkUrl='/life-and-money/how-whole-life-insurance-works/'
                      variant='primaryDeemphasize'>
                      Get more details on how whole life insurance works
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-what-does-it-do-control',
              title: (<>What does whole life <WidowBlock>insurance do&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    The main purpose of whole life insurance is to help make sure your family will be taken care of financially should something happen to you. Coverage is lifelong<sup>1</sup> (unlike term life) and accumulates cash value over time that you can use for anything.<sup>2</sup> Plus, whole life can earn dividends (while not guaranteed, we&#39;ve paid them every year since 1872) that can be taken as cash, used to pay premiums, or buy more coverage.{' '}
                    <Link
                      id='whole-life-insurance-faq-what-does-it-do-fafa-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'>
                      Find your advisor and ask if whole life is right for you
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-can-you-cash-out',
              title: (<>Can you cash out whole <WidowBlock>life insurance&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Yes, there are several ways you can tap into the accumulated cash value of your whole life policy. The best option for you will depend on things like whether you want to keep your coverage or not and how much money you want to access.
                  </Typography>
                  <DescriptionListStyled
                    descriptionListData={[
                      {
                        descriptionTerm: 'Policy surrender:',
                        descriptionDetails: (<>This option cancels your policy outright. We&#39;ll give you back your policy&#39;s cash surrender value (if any), but you won&#39;t be covered anymore.</>),
                      },
                      {
                        descriptionTerm: 'Policy withdrawal:',
                        descriptionDetails: (<>A partial surrender (which surrenders paid&#45;up additions, if any, and releases their cash surrender value) is likely the easiest and fastest way to access the cash in your policy. Keep in mind, by taking money out, you could reduce the long&#45;term growth potential and could leave a smaller death benefit to beneficiaries.</>),
                      },
                      {
                        descriptionTerm: 'Policy loan:',
                        descriptionDetails: (<>You can borrow up to an amount close to the total cash surrender value. However, these loans will accrue interest charges, which can be high. Unless properly managed, a policy&#39;s loan balance can become so large that it causes the policy to lapse, thereby requiring surrender of the policy to obtain repayment of the loan and triggering immediate taxation of any gain inside the policy along with other potential tax consequences. Plus, any money that hasn&#39;t been paid back by the time you pass away will be taken from the amount paid to your beneficiaries.</>),
                      },
                    ]}
                    disableBullet
                  />
                  <Typography disableBottomPadding>
                    Talk to your advisor about which option makes sense for you.{' '}
                    <Link
                      id='whole-life-insurance-faq-talk-to-advisor-fafa-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Get matched with a Northwestern Mutual Financial Advisor'>
                      Get matched today
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-what-are-the-benefits',
              title: (<>What are the benefits of whole <WidowBlock>life insurance&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Whole life insurance is a uniquely flexible asset. Along with a guaranteed payout<sup>1</sup> to your loved ones when you&#39;re no longer here, it accumulates cash value that grows over time. It&#39;s tax advantaged and not directly tied to market fluctuations. Even better, you can use the cash value for anything you want throughout your life.<sup>2</sup> You can also earn dividends (while not guaranteed, we&#39;ve paid them every year since 1872) that can be taken as cash, used to pay premiums, or buy more coverage.{' '}
                    <Link
                      id='whole-life-insurance-faq-compare-types-of-life-insurance-link'
                      linkUrl='/life-insurance/compare-types/'
                      variant='primaryDeemphasize'>
                      See how whole life compares to our other coverage options
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-who-should-consider',
              title: (<>Who should consider whole <WidowBlock>life insurance&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Really, anyone that might benefit from a uniquely flexible asset. Whole life provides financial protection for your loved ones when you&#39;re no longer here. Plus, as you make payments, part of it will accumulate over time. You can use this accumulated cash value for whatever you want, whenever you want.<sup>2</sup>{' '}
                    <Link
                      id='whole-life-insurance-faq-who-should-consider-fafa-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'>
                      Find your advisor and ask if whole life is right for you
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-is-it-a-good-investment-control',
              title: (<>Is whole life insurance a <WidowBlock>good investment&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Whole life insurance is not an investment&#8212;it&#39;s financial protection for your loved ones. Whole life accumulates cash value over time. It&#39;s guaranteed to grow year over year, will never go down (unless you make a policy change) and has tax advantages. Typically, what you earn over what you put in won&#39;t be taxed (unless you withdraw it). Even better, you can use the cash value for anything you want throughout your life.<sup>2</sup>{' '}
                    <Link
                      id='whole-life-insurance-faq-is-it-good-investment-link'
                      linkUrl='/life-and-money/quiz/life-insurance-quiz-how-much-do-you-know/'
                      variant='primaryDeemphasize'>
                      See how much you know about life insurance, take our quiz
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-is-it-worth-it-control',
              title: (<>Is whole life insurance <WidowBlock>worth it&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Think of it this way: If it can help you reach your goals, then yes, it&#39;s worth it. Whole life insurance is a uniquely flexible asset and can be an important part of your financial plan. Along with a guaranteed payout when you&#39;re no longer here, it has more benefits you can use throughout your life. The accumulated cash value is guaranteed to grow year over year. It&#39;s usually tax advantaged and isn&#39;t affected by market fluctuations.{' '}
                    <Link
                      id='whole-life-insurance-faq-is-it-worth-it-link'
                      linkUrl='/life-and-money/what-are-the-benefits-of-whole-life-insurance/'
                      variant='primaryDeemphasize'>
                      Find out more about the benefits of whole life insurance
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-which-is-better-control',
              title: (<>Which is better: term life insurance or whole <WidowBlock>life insurance&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    The best one for you depends on your goals. The biggest differences are that{' '}
                    <Link
                      id='whole-life-insurance-faq-which-is-better-link'
                      linkUrl='/life-insurance/term-life-insurance/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about term life insurance'>
                      term life insurance
                    </Link>
                    {' '}covers you for a limited amount of time and is less likely to be less expensive than whole life insurance. On the other hand, whole life insurance has lifelong coverage and gives you a lot more benefits that you can use throughout your life. Whole life accumulates cash value over time that you can use for anything.<sup>2</sup> Plus, it can earn dividends (while not guaranteed, we&#39;ve paid them every year since 1872) that can be taken as cash, used to pay premiums, or increase your coverage.{' '}
                    <Link
                      id='whole-life-insurance-faq-which-is-better-link'
                      linkUrl='/life-and-money/what-are-the-benefits-of-whole-life-insurance/'
                      variant='primaryDeemphasize'>
                      Find out more about the benefits of whole life Insurance
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-earn-dividends',
              title: (<>Does whole life insurance <WidowBlock>earn dividends&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Yes. Dividends are paid when our claims, expenses, and investment earnings turn out better than the assumptions underlying our policy guarantees. As a mutual company, we don&#39;t have stockholders so instead of paying dividends to investors on Wall Street, we pay them to our policyowners. Even though they're not guaranteed, we&#39;ve paid dividends every year since 1872. You can use them for anything you want when you take it as cash, pay your premiums, or increase your coverage.{' '}
                    <Link
                      id='whole-life-insurance-faq-does-it-earn-dividends-link'
                      linkUrl='/life-and-money/how-do-life-insurance-dividends-work/'
                      variant='primaryDeemphasize'>
                      Find out more on how dividends work
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-riders',
              title: (<>What optional insurance riders does Northwestern <WidowBlock>Mutual offer&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    A rider is a benefit or feature you can add to your life insurance policy, usually for an additional cost. Here&#39;s what you can consider:
                  </Typography>
                  <DescriptionListStyled
                    descriptionListData={[
                      {
                        descriptionTerm: 'Waiver of Premium:',
                        descriptionDetails: (<>We&#39;ll pay your premiums if you become totally disabled from sickness or an accident.</>),
                      },
                      {
                        descriptionTerm: 'Additional Purchase Benefit:',
                        descriptionDetails: (<>You can buy more coverage at certain ages or when you have certain life events, like getting married or having a child, regardless of changes in your health.</>),
                      },
                      {
                        descriptionTerm: 'Accelerated Care Benefit:',
                        descriptionDetails: (<>You can use a some of your death benefit early to help pay for long&#45;term care expenses, like assisted living or help with doing daily tasks.<sup>6</sup></>),
                      },
                    ]}
                    disableBullet
                  />
                  <Typography disableBottomPadding>
                    Check out all of our{' '}
                    <Link
                      id='whole-life-insurance-faq-available-insurance-riders-link'
                      variant='primaryDeemphasize'
                      linkUrl='/life-insurance/life-insurance-riders/'
                      ariaLabel='Learn more about available insurance riders.'>
                      available insurance riders
                    </Link>
                    . Or{' '}
                    <Link
                      id='whole-life-insurance-faq-start-a-conversation-link'
                      variant='primaryDeemphasize'
                      linkUrl='/find-a-financial-advisor/'
                      ariaLabel='Start a conversation with a Northwestern Mutual financial advisor.'>
                      find your advisor
                    </Link>
                    {' '}and talk to them about which rider or riders might best fit your goals.
                  </Typography>
                </>
              ),
            },
            {
              id: 'whole-life-insurance-faq-is-it-same-as-full-life',
              title: (<>Is whole life insurance the same thing as <WidowBlock>full life&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Full life is another name for{' '}
                    <Link
                      id='whole-life-insurance-faq-is-it-same-permanent-link'
                      linkUrl='/life-insurance/permanent-life-insurance/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about permanent life insurance'>
                      permanent life insurance
                    </Link>
                    , which includes whole life,{' '}
                    <Link
                      id='whole-life-insurance-faq-is-it-same-universal-link'
                      linkUrl='/life-insurance/universal-life-insurance/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about universal life insurance'>
                      universal life
                    </Link>
                    {' '}and{' '}
                    <Link
                      id='whole-life-insurance-faq-is-it-same-variable-universal-link'
                      linkUrl='/life-insurance/variable-universal-life-insurance-vul/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about variable universal life insurance'>
                      variable universal life
                    </Link>. Each gives you lifelong coverage<sup>1</sup> and accumulates cash value that you can use for anything you want, at any time.<sup>2</sup> And with whole life, you could have the chance to earn annual dividends, too.<sup>3</sup> Your advisor will help you figure out which is best for you, and exactly how much you need.{' '}
                    <Link
                      id='whole-life-insurance-faq-is-it-same-fafa-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Get matched with a Northwestern Mutual Financial Advisor'>
                      Get matched today
                    </Link>.
                  </Typography>
                </>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
